<template>
  <div v-if="bookedTariff" class="app-current-tariff container">
    <h1 class="sr-only">Tarife und Optionen</h1>
    <h2 class="text-3xl">Mein Tarif</h2>
    <CustomModal :visible="showModal" @close="showModal = false" :showNext="true" @next="showModal = false">
      <template slot="header">
        <slot name="header"> {{ bookedTariff.name }} Tarifdetails</slot>
      </template>
      <template slot="default">
        <ShowMore :initialText="bookedTariff.tariffText"></ShowMore>
        <br />
        <a
          href="/downloads.html?file=files/lidl/downloads/LIDL_Connect_Preisliste.pdf"
          title="Die Datei LIDL_Connect_Preisliste.pdf herunterladen"
          class="text-base"
          >Detaillierte Preisliste herunterladen</a
        >
      </template>
      <template slot="btnPrevTxt">
        <slot name="btnPrevTxt">Schließen</slot>
      </template>
      <template slot="btnNextTxt">
        <slot name="btnNextTxt">Schließen</slot>
      </template>
    </CustomModal>

    <TariffCardCompact @info-click="toggleModal()" :tariff="bookedTariff" class="hide-desktop"></TariffCardCompact>
    <div class="hide-mobile container-box">
      <div class="tariff-desktop-wrapper" :style="{ backgroundColor: getTariffColor }">
        <div class="tariff-name">
          <span class="text-3xl text-semibold">{{ bookedTariff.name }}</span>
        </div>
        <div class="tariff-info">
          <div class="tariff-data">
            <div class="tariff-data-column">
              <span class="text-3xl">{{ getDetailByType('FLAT', 'primary') }}</span>
              <span class="text-sm">{{ getDetailByType('FLAT', 'secondary') }}</span>
            </div>
            <div class="tariff-data-column">
              <span class="text-3xl">{{ getDetailByType('VOLUME', 'primary') }}</span>
              <span class="text-sm">{{ getDetailByType('VOLUME', 'secondary') }}</span>
            </div>

            <div class="tariff-data-column">
              <div class="text-3xl">{{ bookedTariff.formattedBasicFee }}</div>
              <div class="text-sm">für {{ bookedTariff.runtime.amount }} {{ bookedTariff.runtime.formattedUnit }}</div>
            </div>
          </div>
          <button @click="toggleModal()" class="button button--secondary">Tarifdetails</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomModal from '@/components/generic/CustomModal.vue'
import TariffCardCompact from '@/components-v2/flows/LIDL/Overview/TariffCardCompact.vue'
import ShowMore from '@/components-v2/generic/ShowMore.vue'
import slotProxy from '@/mixins/slotProxy'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import extraData from '@/mixins/extraData'
import joinTariffExtra from '@/helper/joinTariffExtras'
import { price } from '@/mixins/price'
import { durationUnit } from '@/mixins/durationUnit'
import { formatter } from '../../../../../../config/calendar'
import { mapActions } from 'vuex'

export default {
  name: 'CurrentTariff',
  components: { TariffCardCompact, CustomModal, ShowMore },
  data() {
    return {
      tariffs: {
        bookedTariff: null,
        pendingTariff: null
      },
      showModal: false
    }
  },
  methods: {
    ...mapActions('bookTariff', ['initiateCancelTariff']),

    compileTariff(tariff, isPending) {
      tariff = joinTariffExtra(tariff, this.extraData.tariffs)
      tariff.details = this.createDetails(tariff, isPending)
      tariff.info = this.createInfo(tariff, isPending)
      tariff.actions = [
        {
          type: 'details',
          text: 'Tarifdetails anzeigen'
        }
      ]
      // if bookableTariffs empty showing change tariff pointless
      if ((this.tariffs.pendingTariff == null || isPending) && this.tariffs.bookableTariffs?.bookableTariffs?.length) {
        tariff.actions.push({
          type: 'changeTariff',
          text: 'Tarif wechseln'
        })
      }
      if (tariff.cancelable) {
        if (isPending && tariff.isSuspendedActive === false) {
          tariff.actions.push({
            type: 'reverseTariff',
            text: 'Stornieren'
          })
        } else if (!this.tariffs.pendingTariff || (isPending && tariff.isSuspendedActive === true)) {
          if (this.isAnnualTariff) {
            tariff.actions.push({
              type: 'cancelTariff',
              text: 'Tarifverlängerung'
            })
          } else {
            tariff.actions.push({
              type: 'cancelTariff',
              text: 'Tarif kündigen'
            })
          }
        }
      }
      return tariff
    },
    cancelTariffAction(bookedTariff) {
      if (this.isAnnualTariff) {
        this.showCanelTariffInfoModal = true
      } else {
        this.initiateCancelTariff().then(response => {
          if (response.success === true) {
            this.$refs.modalAction.showCancelModal(response, bookedTariff.tariffId, bookedTariff)
          }
        })
      }
    },
    reverseTariffAction(pendingTariff) {
      this.$refs.reverseModalAction.showReverseModal(pendingTariff.tariffId, pendingTariff)
    },
    createDetails(tariff, isPending = false) {
      const flatTexts = []
      if (tariff.phoneFlat === 'all') {
        flatTexts.push(this.extraData.flatTexts.phone)
      }
      if (tariff.smsFlat === 'all') {
        flatTexts.push(this.extraData.flatTexts.sms)
      }
      let volumes = []
      if (tariff.tariffExtra !== undefined && tariff.tariffExtra.volumes instanceof Array) {
        volumes = tariff.tariffExtra.volumes
      }
      const priceDetail = {
        primary: price(tariff.basicFee || 0),
        secondary:
          tariff.runtime.amount === 0
            ? null
            : '/' + tariff.runtime.amount + ' ' + durationUnit(tariff.runtime.unit.toUpperCase(), tariff.runtime.amount)
      }
      return [...flatTexts, ...volumes, priceDetail]
    },
    createInfo(tariff, isPending = false) {
      if (isPending && !tariff.isSuspendedActive) {
        tariff.statusKey = 'IDL'
      } else if (isPending && tariff.isSuspendedActive) {
        tariff.statusKey = 'SUSA'
      } else if (tariff.statusKey === 'CDL') {
        if (tariff.runtime.amount === 0) {
          tariff.statusKey = 'ACT'
        } else {
          tariff.statusKey = 'CON'
        }
      }
      let date = '-'
      const dateKey = this.extraData.infoTexts[tariff.statusKey].date
      if (dateKey && tariff[dateKey]) {
        date = formatter.dateFormatter.format(new Date(tariff[dateKey]))
      }
      let info = this.extraData.infoTexts[tariff.statusKey].text.replace('%DATE%', date)
      if (this.isAnnualTariff()) {
        info = 'Endet zum ' + date
      }
      const infoResult = {
        name: tariff.name,
        info: info
      }
      if (isPending) {
        infoResult.prepend = this.extraData.prependTexts?.pendingTariff
          ? this.extraData.prependTexts?.pendingTariff
          : 'Mein neuer Tarif'
      }
      return infoResult
    },
    getDetailByType(type, key) {
      const item = this.tariffs?.bookedTariff.extraDataDetails.find(detail => detail.type === type)
      return item ? item[key] : ''
    },
    refreshQuery() {
      this.$apollo.queries.tariffs.refetch()
    },
    isAnnualTariff() {
      return this.tariffs.bookedTariff.tariffExtra?.type === 'year'
    },
    toggleModal() {
      this.showModal = !this.showModal
    }
  },
  computed: {
    bookedTariff() {
      if (this.tariffs.bookedTariff) {
        return this.compileTariff(this.tariffs.bookedTariff, false)
      }
      return null
    },
    pendingTariff() {
      if (this.tariffs.pendingTariff) {
        return this.compileTariff(this.tariffs.pendingTariff, true)
      }
      return null
    },
    getTariffColor() {
      return this.bookedTariff ? `var(--tariff-${this.bookedTariff.tariffId})` : 'var(--tariff-default)'
    }
  },
  mixins: [slotProxy(), extraData()],
  props: {
    tariffPageLink: {
      type: String,
      default: null
    }
  },
  apollo: {
    tariffs: function() {
      return wrapQuery(
        gql`
          query bookedTariff {
            tariffs {
              bookableTariffs {
                bookableTariffs {
                  tariffId
                }
              }
              bookedTariff {
                bandwidth {
                  down
                  up
                }
              extraDataDetails {
                  primary
                  secondary
                  type
                }
                tariffId
                tariffText
                name
                basicFee
                statusKey
                smsFlat
                cancelable
                tariffChangePossible
                isPendingTariff
                isSuspendedActive
                phoneFlat
                tariffState
                renewContractDate
                possibleChangingDate
                terminationDate
                changeTariffDate
                runtime {
                  amount
                  unit
                  formattedUnit
                }
                volumes {
                  amount
                  formattedUnit
                  type
                  unit
                }
              }
              pendingTariff {
                tariffId
                name
                tariffState
                basicFee
                tariffChangePossible
                isPendingTariff
                isSuspendedActive
                cancelable
                statusKey
                smsFlat
                phoneFlat
                phoneFlat
                tariffState
                renewContractDate
                possibleChangingDate
                terminationDate
                changeTariffDate
                runtime {
                  amount
                  unit
                }
                volumes {
                  amount
                  formattedUnit
                  type
                  unit
                }
              }
            }
          }
        `,
        this,
        'tariffs'
      )
    }
  }
}
</script>
